import { platformApiClient } from '@/services/platform-api-client';

/**
 * Platform Console — Email Template Master API.
 *
 * Backs COMMUNICATION_DESIGN.md §4. Every endpoint here already exists in the
 * deployed Phase A backend (`platform/email-template-masters`); Phase B adds no
 * new endpoint and no schema change.
 *
 * NOTE: the platformApiClient response interceptor already unwraps
 * { data: T, meta } → T, so `r.data` is the payload. Never double-unwrap.
 */

// ── Types (mirror the backend DTOs exactly — see §4.2 / §4.3) ─────────────────

export interface EmailTemplateMaster {
  id: string;
  templateKey: string;
  name: string;
  category: string;
  description?: string | null;
  channelType: string;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  status: string;
  isArchived: boolean;
  currentVersion: number;
  createdById?: string | null;
  updatedById?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Release notes as STORED on a published version.
 *
 * `variablesAdded` / `variablesRemoved` are computed by the backend
 * (`publishVersion()` runs `diffVariables()` against the previous version) and
 * are read-only here — they appear in version history but are never submitted.
 */
export interface ReleaseNotes {
  summary: string;
  featuresAdded: string[];
  featuresRemoved: string[];
  variablesAdded: string[];
  variablesRemoved: string[];
  breakingChanges: string;
}

/**
 * Release notes as ACCEPTED by `POST :id/publish`.
 *
 * Deliberately excludes the variable delta: `ReleaseNotesDto` does not declare
 * those fields, and the API runs `whitelist` + `forbidNonWhitelisted`, so
 * sending them fails the request with
 * `releaseNotes.property variablesAdded should not exist`. They are the
 * server's to compute, not the client's to supply.
 */
export type PublishReleaseNotesInput = Omit<ReleaseNotes, 'variablesAdded' | 'variablesRemoved'>;

export interface EmailTemplateMasterVersion {
  id: string;
  masterId: string;
  versionNumber: number;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  changeNote?: string | null;
  changedByName?: string | null;
  changedByRole?: string | null;
  releaseNotes?: ReleaseNotes | null;
  createdAt: string;
}

export interface TemplateUsage {
  templateKey: string;
  usedBy: string[];
  modules: string[];
}

export interface RenderedPreview {
  subject: string;
  htmlBody: string;
  plainTextBody: string;
}

export interface SendTestResult {
  success: boolean;
  recipient: string;
  message: string;
  resolvedFrom?: string;
}

export interface MasterListParams {
  search?: string;
  status?: string;
  category?: string;
  isArchived?: boolean;
}

const BASE = '/platform/email-template-masters';

// ── Reads ────────────────────────────────────────────────────────────────────

export async function listMasters(params: MasterListParams = {}): Promise<EmailTemplateMaster[]> {
  // Build conditionally so an unset filter is never sent as an empty param.
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.status) query.status = params.status;
  if (params.category) query.category = params.category;
  if (params.isArchived !== undefined) query.isArchived = String(params.isArchived);

  const r = await platformApiClient.get(BASE, { params: query });
  const data = r.data as unknown;
  return Array.isArray(data) ? (data as EmailTemplateMaster[]) : [];
}

export interface VariableDefinition {
  key: string;
  label: string;
  module: string;
  category: string[];
  exampleValue: string;
  description?: string;
}

export async function listVariables(): Promise<VariableDefinition[]> {
  const r = await platformApiClient.get(`${BASE}/variables`);
  const data = r.data as unknown;
  return Array.isArray(data) ? (data as VariableDefinition[]) : [];
}

export async function getMaster(id: string): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.get(`${BASE}/${id}`);
  return r.data as EmailTemplateMaster;
}

export async function getVersions(id: string): Promise<EmailTemplateMasterVersion[]> {
  const r = await platformApiClient.get(`${BASE}/${id}/versions`);
  const data = r.data as unknown;
  return Array.isArray(data) ? (data as EmailTemplateMasterVersion[]) : [];
}

export async function getUsage(id: string): Promise<TemplateUsage> {
  const r = await platformApiClient.get(`${BASE}/${id}/usage`);
  return r.data as TemplateUsage;
}

export async function previewMaster(
  id: string,
  context?: Record<string, unknown>,
): Promise<RenderedPreview> {
  const r = await platformApiClient.post(`${BASE}/${id}/preview`, context ? { context } : {});
  return r.data as RenderedPreview;
}

// ── Writes (require platform:templates:manage) ───────────────────────────────

export interface CreateMasterInput {
  templateKey: string;
  name: string;
  category: string;
  description?: string;
  subject: string;
  htmlBody: string;
  plainTextBody: string;
}

export async function createMaster(input: CreateMasterInput): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.post(BASE, input);
  return r.data as EmailTemplateMaster;
}

export interface UpdateMasterInput {
  name?: string;
  category?: string;
  description?: string;
  subject?: string;
  htmlBody?: string;
  plainTextBody?: string;
  changeNote?: string;
}

export async function updateMaster(
  id: string,
  input: UpdateMasterInput,
): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.patch(`${BASE}/${id}`, input);
  return r.data as EmailTemplateMaster;
}

export async function publishMaster(
  id: string,
  releaseNotes: PublishReleaseNotesInput,
): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.post(`${BASE}/${id}/publish`, { releaseNotes });
  return r.data as EmailTemplateMaster;
}

export async function cloneMaster(
  id: string,
  templateKey: string,
  name: string,
): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.post(`${BASE}/${id}/clone`, { templateKey, name });
  return r.data as EmailTemplateMaster;
}

export async function activateMaster(id: string): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.patch(`${BASE}/${id}/activate`);
  return r.data as EmailTemplateMaster;
}

export async function disableMaster(id: string): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.patch(`${BASE}/${id}/disable`);
  return r.data as EmailTemplateMaster;
}

export async function archiveMaster(id: string): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.patch(`${BASE}/${id}/archive`);
  return r.data as EmailTemplateMaster;
}

export async function rollbackMaster(id: string, version: number): Promise<EmailTemplateMaster> {
  const r = await platformApiClient.patch(`${BASE}/${id}/rollback/${version}`);
  return r.data as EmailTemplateMaster;
}

export async function sendTestMaster(
  id: string,
  recipient: string,
  context?: Record<string, unknown>,
): Promise<SendTestResult> {
  const body: Record<string, unknown> = { recipient };
  if (context) body.context = context;
  const r = await platformApiClient.post(`${BASE}/${id}/send-test`, body);
  return r.data as SendTestResult;
}

export async function deleteMaster(id: string): Promise<void> {
  await platformApiClient.delete(`${BASE}/${id}`);
}
